/**
 * HubCategoryOverview — the "space home" for one Hub category: a description plus its
 * documents as expandable cards (ownership, a short scan-the-document summary, and a table of
 * contents per document), the Guru/Document360 "knowledge base home" pattern. Navigating into a
 * specific document is the tree's job now (HubLayout); this pane is for landing on a category
 * and previewing what's in it without opening each one.
 */
import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { documentApi, documentsApi, peopleApi, departmentsApi } from '../../services/api';
import {
  HUB_CATEGORIES, STATUS_META, effectiveStatus, formatUpdated,
  buildDocTree, flattenDocTree, extractHeadingsFromHtml, extractSummary,
} from './hubUtils';

interface HubDoc {
  documentId: string;
  docNumber?: string;
  title?: string;
  domainArea?: string;
  documentType?: string;
  status?: string;
  nextReviewDate?: string;
  author?: string;
  departmentId?: string;
  uploadedAt?: string;
  updatedAt?: number;
  fileName?: string;
  parentDocumentId?: string;
  content?: string;
}

export const HubCategoryOverview: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const cat = HUB_CATEGORIES.find((c) => c.key === category);

  const { data: registers = [], isLoading: registersLoading } = useQuery<any[]>({
    queryKey: ['documentRegisters'],
    queryFn: documentApi.getRegisters,
    enabled: !cat?.dpiaOnly,
  });
  const register = registers.find((r) => r.name === cat?.registerName);

  // DPIAs are entity-linked, not register-linked - same reasoning as HubLayout's own
  // useCategoryDocs, kept in sync with it deliberately rather than sharing one hook, since this
  // page's query also needs `content` for the TL;DR/contents preview that the tree sidebar
  // never renders.
  const { data: allDocs = [], isLoading: allDocsLoading, error: allDocsError } = useQuery<HubDoc[]>({
    queryKey: ['hubDocsAll'],
    queryFn: documentApi.listAll,
    enabled: !!cat?.dpiaOnly,
    staleTime: 0,
  });
  const { data: registerDocs = [], isLoading: registerDocsLoading, error: registerDocsError } = useQuery<HubDoc[]>({
    queryKey: ['hubDocs', register?.registerId],
    queryFn: () => documentApi.listByRegister(register.registerId),
    enabled: !cat?.dpiaOnly && !!register,
    staleTime: 0,
  });
  const docs = cat?.dpiaOnly ? allDocs.filter((d) => d.documentType === 'DPIA') : registerDocs;
  const docsLoading = cat?.dpiaOnly ? allDocsLoading : registerDocsLoading;
  const error = cat?.dpiaOnly ? allDocsError : registerDocsError;

  // Same resolution HubDocumentPage's own meta-rail uses for Owner/Team - fetched once here and
  // passed down rather than once per card, since every card in the category shares one cache.
  const { data: people = [] } = useQuery<any[]>({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const { data: departments = [] } = useQuery<any[]>({ queryKey: ['departments'], queryFn: departmentsApi.getAll });
  const personName = (personId: string) => people.find((p) => p.personId === personId)?.personName || personId;
  const departmentName = (departmentId: string) => departments.find((d) => d.departmentId === departmentId)?.name || departmentId;

  // Ids the user has explicitly clicked open - starting empty means every master with
  // subordinates (and every card's own detail panel) is collapsed by default, with no need to
  // already know which ids will turn out to have children before any data has loaded.
  const [expandedMasters, setExpandedMasters] = useState<Record<string, boolean>>({});
  const [detailsOpen, setDetailsOpen] = useState<Record<string, boolean>>({});
  const ordered = useMemo(
    () => [...docs].sort((a, b) => (a.uploadedAt || '').localeCompare(b.uploadedAt || '')),
    [docs],
  );
  const tree = useMemo(() => buildDocTree(ordered), [ordered]);
  const rows = useMemo(
    () => flattenDocTree(tree, (id) => !!expandedMasters[id]),
    [tree, expandedMasters],
  );

  if (!cat) {
    return <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Unknown Hub category.</div>;
  }

  const isLoading = cat.dpiaOnly ? docsLoading : (registersLoading || (!!register && docsLoading));

  return (
    <div>
      <PageHeader
        icon={cat.icon}
        title={cat.label}
        subtitle={cat.desc}
        breadcrumbs={[{ label: 'Hub' }, { label: cat.shortLabel }]}
      />

      {isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
        </div>
      ) : error ? (
        <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Failed to load this section.</div>
      ) : (!cat.dpiaOnly && !register) || ordered.length === 0 ? (
        <div className="np-card">
          <div className="np-card-body py-5 text-center">
            <i className={cat.icon} style={{ fontSize: '3rem', color: 'var(--bs-secondary-color)' }}></i>
            <h6 className="mt-3 np-sub">Nothing published in this section yet.</h6>
          </div>
        </div>
      ) : (
        <div className="hub-overview-list">
          {rows.map(({ doc, depth, hasChildren }) => (
            <HubOverviewDocCard
              key={doc.documentId}
              doc={doc}
              depth={depth}
              hasChildren={hasChildren}
              isNestCollapsed={!expandedMasters[doc.documentId]}
              onToggleNest={() => setExpandedMasters((prev) => ({ ...prev, [doc.documentId]: !prev[doc.documentId] }))}
              isDetailsOpen={!!detailsOpen[doc.documentId]}
              onToggleDetails={() => setDetailsOpen((prev) => ({ ...prev, [doc.documentId]: !prev[doc.documentId] }))}
              personName={personName}
              departmentName={departmentName}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const HubOverviewDocCard: React.FC<{
  doc: HubDoc;
  depth: number;
  hasChildren: boolean;
  isNestCollapsed: boolean;
  onToggleNest: () => void;
  isDetailsOpen: boolean;
  onToggleDetails: () => void;
  personName: (personId: string) => string;
  departmentName: (departmentId: string) => string;
}> = ({ doc, depth, hasChildren, isNestCollapsed, onToggleNest, isDetailsOpen, onToggleDetails, personName, departmentName }) => {
  const status = effectiveStatus(doc);
  const meta = STATUS_META[status];

  // Parsing content into headings/a summary is real work (a DOMParser pass over the whole
  // document) - only done for a card that's actually open, and cached per document so
  // toggling every other card's details on this page doesn't re-run it.
  const headings = useMemo(
    () => (isDetailsOpen ? extractHeadingsFromHtml(doc.content || '') : []),
    [isDetailsOpen, doc.content],
  );
  const summary = useMemo(
    () => (isDetailsOpen ? extractSummary(doc.content || '') : ''),
    [isDetailsOpen, doc.content],
  );

  return (
    <div className="hub-overview-card">
      <div className="hub-overview-row" style={{ paddingLeft: `${depth * 1.25}rem` }}>
        {hasChildren ? (
          <button
            type="button"
            className="np-icon-btn flex-shrink-0"
            title={isNestCollapsed ? 'Expand subordinate policies' : 'Collapse subordinate policies'}
            onClick={onToggleNest}
          >
            <i className={isNestCollapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'}></i>
          </button>
        ) : (
          <i className={depth === 0 ? 'ri-file-text-line' : 'ri-subtract-line'} style={{ color: 'var(--bs-secondary-color)', flexShrink: 0, width: '1.875rem', textAlign: 'center' }}></i>
        )}
        <div className="flex-grow-1 min-width-0">
          <Link to={`/hub/documents/${encodeURIComponent(doc.documentId)}`} className="hub-overview-title text-truncate d-block">
            {doc.title || 'Untitled'}
          </Link>
          {doc.docNumber && <span className="np-hint np-tabular">{doc.docNumber}</span>}
        </div>
        <span className={`np-chip ${meta.tone} flex-shrink-0`}><i className={meta.icon}></i>{meta.label}</span>
        <span className="np-sub flex-shrink-0 d-none d-md-inline">{formatUpdated(doc.updatedAt)}</span>
        {doc.fileName && (
          <button
            type="button"
            className="np-icon-btn flex-shrink-0"
            title="Download original PDF"
            onClick={() => documentsApi.download(doc.documentId, doc.fileName || `${doc.title}.pdf`)}
          >
            <i className="ri-download-2-line"></i>
          </button>
        )}
        <button
          type="button"
          className={`hub-overview-expand-btn flex-shrink-0 ${isDetailsOpen ? 'open' : ''}`}
          title={isDetailsOpen ? 'Hide details' : 'Show ownership, summary, and contents'}
          onClick={onToggleDetails}
        >
          <i className="ri-arrow-down-s-line"></i>
        </button>
      </div>

      {isDetailsOpen && (
        <div className="hub-overview-details">
          <div className="np-row-card mb-0">
            <div className="hub-overview-subcard-head"><i className="ri-team-line"></i>Ownership</div>
            <div className="hub-overview-owner-row">
              <span className="hub-overview-avatar">{initials(doc.author ? personName(doc.author) : '')}</span>
              <div>
                <div className="hub-overview-owner-name">{doc.author ? personName(doc.author) : <span className="np-sub">Not set</span>}</div>
                <div className="np-hint">Author</div>
              </div>
            </div>
            <div className="hub-overview-owner-row">
              <span className="hub-overview-avatar">{initials(doc.departmentId ? departmentName(doc.departmentId) : '')}</span>
              <div>
                <div className="hub-overview-owner-name">{doc.departmentId ? departmentName(doc.departmentId) : <span className="np-sub">Not set</span>}</div>
                <div className="np-hint">Team</div>
              </div>
            </div>
          </div>

          <div className="np-row-card mb-0">
            <div className="hub-overview-subcard-head"><i className="ri-file-list-3-line"></i>TL;DR</div>
            <p className="np-sub mb-0" style={{ fontStyle: summary ? 'normal' : 'italic' }}>
              {summary || 'Nothing to summarise yet.'}
            </p>
          </div>

          <div className="np-row-card mb-0">
            <div className="hub-overview-subcard-head"><i className="ri-list-check-2"></i>Contents</div>
            {headings.length === 0 ? (
              <p className="np-sub mb-0 fst-italic">No headings found.</p>
            ) : (
              <ul className="hub-overview-toc">
                {headings.map((h) => (
                  <li key={h.id} style={{ paddingLeft: `${h.level * 0.75}rem` }}>
                    <Link to={`/hub/documents/${encodeURIComponent(doc.documentId)}#${h.id}`}>{h.text}</Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export default HubCategoryOverview;
